import React from 'react';
import moment from 'moment';
import {
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LenderPerformanceReportFilters } from '@trnsact/trnsact-shared-types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { LOOK_TO_BOOK_CHART_HEIGHT, LOOK_TO_BOOK_HEATMAP_MAX_HEIGHT } from '../../../../constants';
import {
  formatMonthLabel,
  formatPercentValue,
  getLookToBookHeatmapColor,
  getLookToBookHeatmapTextColor,
  mapMonthlyLookToBookByLenderHeatmap,
} from '../../../../lib';
import { LookToBookHeatmapLegend } from './LookToBookHeatmapLegend';
import { AsyncResource } from '../../../../types';

interface Props {
  resource: AsyncResource<any[] | null>;
  filters?: LenderPerformanceReportFilters | null;
}

const STICKY_COLUMN_SX = { position: 'sticky' as const, left: 0, zIndex: 2, backgroundColor: 'background.paper' };

export const MonthlyLookToBookByLenderChart = ({ resource, filters }: Props) => {
  const { months, rows: heatmapRows } = mapMonthlyLookToBookByLenderHeatmap(resource.data ?? []);
  const created = filters?.created;
  const period = created
    ? `${moment(created.from).format('MMM D, YYYY')} – ${moment(created.to).format('MMM D, YYYY')}`
    : '';
  const subTitle = `Look-to-Book trend per lender — ${period}`;

  return (
    <CardContainer title="Monthly Look-to-Book by Lender" subTitle={subTitle}>
      {resource.loading ? (
        <Skeleton variant="rounded" height={LOOK_TO_BOOK_CHART_HEIGHT} />
      ) : heatmapRows.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No data available for the selected filters
        </Typography>
      ) : (
        <Stack gap="0.75rem">
          <LookToBookHeatmapLegend />

          <TableContainer sx={{ maxHeight: LOOK_TO_BOOK_HEATMAP_MAX_HEIGHT }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...STICKY_COLUMN_SX, zIndex: 3 }}>Lender</TableCell>
                  {months.map(month => (
                    <TableCell key={month} align="center">
                      {formatMonthLabel(month)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {heatmapRows.map(row => (
                  <TableRow key={row.key}>
                    <TableCell sx={{ ...STICKY_COLUMN_SX, fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {row.label}
                    </TableCell>

                    {row.cells.map(cell => (
                      <TableCell
                        key={cell.month}
                        align="center"
                        sx={{
                          backgroundColor: getLookToBookHeatmapColor(cell.value),
                          color: getLookToBookHeatmapTextColor(cell.value),
                        }}
                      >
                        {cell.value == null ? '–' : formatPercentValue(cell.value)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </CardContainer>
  );
};
